// Verbindet Zustand (zustand.ts), lokalen Speicher (speicher.ts) und
// Warteschlange (sync.ts). Die Oberfläche ruft nur Aktionen dieses Stores
// auf und liest den Zustand per useSyncExternalStore.
//
// Jede Aktion: 1. Zustand ändern, 2. lokal speichern, 3. Auftrag einreihen.
// Fotos werden VOR der Zustandsänderung lokal abgelegt, damit ein Foto nie
// nur im Arbeitsspeicher existiert.

import {
  addressSchema,
  surveyPatchSchema,
  type HallSide,
  type OpeningKind,
  type PhotoStep,
  type Problem,
  type RoomDoorKind,
  type RoomType,
} from "@umzugsscan/shared";
import { ApiError, NetworkError, type Api } from "./api.ts";
import { entranceHall } from "./ablauf.ts";
import type { LocalDb } from "./speicher.ts";
import { SyncQueue, type SyncStatus } from "./sync.ts";
import {
  addPhoto,
  fromServer,
  markUploaded,
  patchAddress,
  patchSurvey,
  reconcileRooms,
  removePhoto,
  removeRoom,
  roomName,
  setMarks,
  setNav,
  upsertRoom,
  type Address,
  type Draft,
  type Mark,
  type Nav,
  type Room,
  type ServerState,
  type SurveyFields,
} from "./zustand.ts";

/** Antwort der Flur-Erkennung (POST /erfassung/:token/flur/:roomId). */
export type Opening = { photoId: string; x: number; y: number; section?: number; side: HallSide; order: number; kind: OpeningKind; guess: RoomType | null; note: string };
export type HallScan = { available: boolean; layout: string; openings: Opening[] };
/** Türen je Raum (15.10.2026): von der KI auf den Wandfotos gefunden. */
export type FoundDoor = { photoId: string; x: number; y: number; wall: "gegenueber" | "rechts" | "links" | "tuer"; kind: RoomDoorKind; entry: boolean; guess: RoomType | null; note: string };
export type DoorScan = { available: boolean; openings: FoundDoor[] };

export type ProcessedImage = { blob: Blob; blurScore: number | null; brightness: number | null };

export function newId(): string {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  // Ältere Safari-Versionen: UUID v4 aus Zufallsbytes.
  const b = crypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6]! & 0x0f) | 0x40;
  b[8] = (b[8]! & 0x3f) | 0x80;
  const h = [...b].map((x) => x.toString(16).padStart(2, "0")).join("");
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

/**
 * Nur Felder, die der Server annimmt. Halb getippte Werte (E-Mail ohne
 * Domain, PLZ mit 3 Ziffern) bleiben lokal, bis sie gültig sind. Sonst würde
 * der Server den ganzen (verschmolzenen) Auftrag ablehnen und gültige Felder
 * gingen mit verloren.
 */
export function serverFields(
  patch: Record<string, unknown>,
  schema: { safeParse(v: unknown): { success: boolean; data?: unknown } },
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(patch)) {
    const r = schema.safeParse({ [k]: v });
    if (r.success) Object.assign(out, r.data as object);
  }
  return out;
}

export class DraftStore {
  private state: Draft;
  private status: SyncStatus = { pending: 0, state: "gespeichert" };
  private listeners = new Set<() => void>();
  private persistChain: Promise<unknown> = Promise.resolve();
  readonly sync: SyncQueue;
  private readonly db: LocalDb;
  private readonly api: Api;

  private constructor(db: LocalDb, api: Api, initial: Draft, opts: { delayMs?: number } = {}) {
    this.db = db;
    this.api = api;
    this.state = initial;
    this.sync = new SyncQueue({
      token: initial.token,
      db,
      api,
      delayMs: opts.delayMs,
      onStatus: (s) => {
        this.status = s;
        this.emit();
      },
      onDone: (op) => {
        if (op.kind === "photo") this.commit(markUploaded(this.state, op.photoId));
      },
      onSubmittedElsewhere: () => void this.reloadFromServer(),
    });
  }

  /**
   * Öffnet eine Aufnahme. Gibt es lokal noch offene Aufträge oder keine
   * Verbindung, gilt der lokale Stand; sonst der Server (z. B. wenn auf einem
   * anderen Gerät weitergemacht wurde).
   */
  static async open(token: string, db: LocalDb, api: Api, opts: { delayMs?: number } = {}): Promise<DraftStore> {
    const local = await db.getDraft(token);
    const pending = await db.countOps(token);
    let draft: Draft | undefined;
    if (local && pending > 0) {
      draft = local;
    } else {
      try {
        draft = fromServer(await api.load<ServerState>(token), local);
      } catch (e) {
        if (!(e instanceof NetworkError) || !local) throw e;
        draft = local;
      }
    }
    const { savedAt: _, ...clean } = draft as Draft & { savedAt?: string };
    const store = new DraftStore(db, api, clean, opts);
    await db.putDraft(store.state);
    await store.sync.init();
    if (pending > 0) store.sync.kick();
    return store;
  }

  // ------------------------------------------------------------ Lesen

  subscribe = (fn: () => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };
  getSnapshot = () => this.state;
  getStatus = () => this.status;

  private emit() {
    for (const l of this.listeners) l();
  }

  private commit(next: Draft) {
    if (next === this.state) return;
    this.state = next;
    // Schreibvorgänge nacheinander, damit nie ein älterer Stand einen neueren überschreibt.
    const snapshot = next;
    this.persistChain = this.persistChain.then(() => this.db.putDraft(snapshot)).catch((e) => console.error(e));
    this.emit();
  }

  /** Wartet, bis der lokale Stand geschrieben ist (Tests, Absenden). */
  flushLocal() {
    return this.persistChain;
  }

  private get base() {
    return this.api.survey(this.state.token);
  }

  // ------------------------------------------------------------ Aktionen

  updateSurvey(patch: Partial<SurveyFields>) {
    this.commit(patchSurvey(this.state, patch));
    // consentPrivacy: false fällt hier heraus (per API nur erteilbar, nicht zurücknehmbar).
    const body = serverFields(patch, surveyPatchSchema);
    if (Object.keys(body).length > 0) void this.sync.enqueue({ token: this.state.token, kind: "json", method: "PATCH", path: this.base, body });
  }

  updateAddress(kind: "from" | "to", patch: Partial<Address>) {
    this.commit(patchAddress(this.state, kind, patch));
    const body = serverFields(patch, addressSchema);
    if (Object.keys(body).length > 0)
      void this.sync.enqueue({ token: this.state.token, kind: "json", method: "PUT", path: `${this.base}/adressen/${kind}`, body });
  }

  go(nav: Partial<Nav>) {
    this.commit(setNav(this.state, nav));
    const n = this.state.nav;
    // Fortschritt auch am Server, damit es auf einem anderen Gerät an der gleichen Stelle weitergeht.
    void this.sync.enqueue({
      token: this.state.token,
      kind: "json",
      method: "PATCH",
      path: this.base,
      body: { progress: { screen: n.screen, guide: n.guide, phase: n.phase } },
    });
  }

  /** Raum anlegen oder ändern (Grundriss). */
  saveRoom(room: Room) {
    this.putRoom(room);
  }

  /**
   * Flur der Eingangsebene anlegen bzw. in den Grundriss aufnehmen. Liest den
   * aktuellen Stand (nicht den des letzten Renderns), damit doppelte Effekte
   * (React StrictMode, schnelles Zurück/Weiter) keinen zweiten Flur anlegen.
   */
  ensureEntranceHall(): Room {
    const hall = entranceHall(this.state.rooms);
    if (hall && hall.hallOrder === 0 && (hall.floor ?? 0) === 0) return hall;
    const room: Room = hall
      ? { ...hall, floor: 0, hallOrder: 0 }
      : { id: newId(), type: "flur", name: roomName("flur", 1), sort: 0, confirmedComplete: false, floor: 0, hallOrder: 0 };
    this.putRoom(room);
    return room;
  }

  private putRoom(room: Room) {
    this.commit(upsertRoom(this.state, room));
    const { id, ...body } = room;
    void this.sync.enqueue({ token: this.state.token, kind: "json", method: "PUT", path: `${this.base}/raeume/${id}`, body });
  }

  /** Räume an die gewählten Anzahlen angleichen; liefert entfernte Räume. */
  setRoomCounts(counts: Partial<Record<RoomType, number>>): Room[] {
    const { add, remove } = reconcileRooms(this.state.rooms, counts, newId);
    for (const r of remove) this.deleteRoom(r.id);
    for (const r of add) this.putRoom(r);
    return remove;
  }

  /** Antwort auf "Gibt es hier große Schränke?" (rooms.cabinets). */
  setCabinets(id: string, cabinets: "ja" | "nein") {
    const room = this.state.rooms.find((r) => r.id === id);
    if (room && room.cabinets !== cabinets) this.putRoom({ ...room, cabinets });
  }

  confirmRoom(id: string, confirmed = true) {
    const room = this.state.rooms.find((r) => r.id === id);
    if (room && room.confirmedComplete !== confirmed) this.putRoom({ ...room, confirmedComplete: confirmed });
  }

  deleteRoom(id: string) {
    const photos = this.state.photos.filter((p) => p.roomId === id);
    this.commit(removeRoom(this.state, id));
    for (const p of photos) {
      void this.sync.dropPhoto(p.id);
      void this.db.deleteBlob(p.id);
    }
    void this.sync.enqueue({ token: this.state.token, kind: "json", method: "DELETE", path: `${this.base}/raeume/${id}` });
  }

  async addPhoto(roomId: string, step: PhotoStep, img: ProcessedImage, section?: number): Promise<string> {
    const id = newId();
    await this.db.putBlob(this.state.token, id, img.blob);
    this.commit(
      addPhoto(this.state, {
        id,
        roomId,
        step,
        section: section ?? null,
        blurScore: img.blurScore,
        brightness: img.brightness,
        marks: [],
        uploaded: false,
        url: null,
        createdAt: new Date().toISOString(),
      }),
    );
    await this.sync.enqueue({
      token: this.state.token,
      kind: "photo",
      photoId: id,
      roomId,
      step,
      section: section ?? null,
      blurScore: img.blurScore,
      brightness: img.brightness,
    });
    return id;
  }

  deletePhoto(id: string) {
    this.commit(removePhoto(this.state, id));
    void this.sync.dropPhoto(id).then(() => this.db.deleteBlob(id));
    void this.sync.enqueue({ token: this.state.token, kind: "json", method: "DELETE", path: `${this.base}/fotos/${id}` });
  }

  setMarks(photoId: string, marks: Mark[]) {
    this.commit(setMarks(this.state, photoId, marks));
    void this.sync.enqueue({
      token: this.state.token,
      kind: "json",
      method: "PUT",
      path: `${this.base}/fotos/${photoId}/markierungen`,
      body: marks.map((m) => ({ x: m.x, y: m.y, action: m.action, label: m.label ?? undefined })),
    });
  }

  photoBlob(photoId: string) {
    return this.db.getBlob(photoId);
  }

  /**
   * KI-Prüfung der Fotos. Erst alles übertragen, damit die KI den aktuellen
   * Stand sieht. Unveränderte Räume prüft der Server nicht erneut.
   */
  async precheck(): Promise<{ available: boolean; problems: Problem[] }> {
    await this.sync.waitIdle();
    return this.api.precheck(this.state.token);
  }

  /** Grundriss: Türen im Flur erkennen lassen (erst die Flurfotos übertragen). */
  async scanRoomDoors(roomId: string): Promise<DoorScan> {
    await this.sync.waitIdle();
    return this.api.roomDoors<DoorScan>(this.state.token, roomId);
  }

  async scanHall(roomId: string): Promise<HallScan> {
    await this.sync.waitIdle();
    return this.api.hall<HallScan>(this.state.token, roomId);
  }

  /**
   * Absenden: erst alles übertragen, dann absenden. Bei fehlenden Angaben
   * wirft der Server ApiError("unvollstaendig") mit details.problems.
   */
  async submit(): Promise<void> {
    await this.sync.waitIdle();
    const server = await this.api.submit<ServerState>(this.state.token);
    this.sync.stop();
    this.commit(fromServer(server, null));
    // Fotos sind beim Server; lokal nur noch der Danke-Zustand.
    await this.db.deleteBlobsForToken(this.state.token);
  }

  /** Stand neu vom Server holen (Kunden-Version: warten auf die Auswertung). */
  refresh() {
    return this.reloadFromServer();
  }

  /** Kunden-Version: Aufnahme beim Server und auf dem Gerät löschen. */
  async remove(): Promise<void> {
    await this.api.request("DELETE", this.api.survey(this.state.token));
    this.sync.stop();
    await this.db.deleteBlobsForToken(this.state.token);
    await this.db.deleteDraft(this.state.token);
  }

  private async reloadFromServer() {
    try {
      this.commit(fromServer(await this.api.load<ServerState>(this.state.token), null));
    } catch (e) {
      if (!(e instanceof ApiError || e instanceof NetworkError)) throw e;
    }
  }
}
